import React from "react";
import { Typography } from "@mui/material";
import type { Topic } from "~/lqs";
import type { Maybe } from "~/types";
import { usePlayerActions } from "../actions";
import { TopicSelect } from "../components/TopicSelect";
import type { InitializedPanelNode } from "../panels";
import type { ImageFrame } from "../visualizations/ImageVisualization/types";
import {
  DetectionClassificationControls,
  DetectionResultsControls,
} from "./detections";
import { ImageResultsControls } from "./images";
import { isInferenceTopic } from "./inference-config";
import { usePanelOnDemandInference } from "./on-demand-inference";
import { ImageSegmentationResultsControls } from "./segmentations";
import type { InferenceTopicSearchResult } from "./utils";

export function InferenceFrameControls({
  panel,
  topic,
  playerTopics,
  inferenceTopicSearchResult,
  imageFrame,
}: {
  panel: InitializedPanelNode;
  topic: Topic;
  playerTopics: ReadonlyArray<Topic>;
  inferenceTopicSearchResult: InferenceTopicSearchResult;
  imageFrame: Maybe<ImageFrame>;
}) {
  const playerActions = usePlayerActions();

  const inferenceTopics = filterInferenceTopics(topic, playerTopics);

  const showingOnDemandResults =
    usePanelOnDemandInference(panel).status === "fulfilled";

  function handleInferenceTopicChange(newValue: Topic | null) {
    playerActions.setInferenceTopic(panel, newValue);
  }

  let typeSpecificControls = null;
  if (showingOnDemandResults) {
    typeSpecificControls = (
      <Typography>
        Inference task controls hidden when viewing an on-demand result
      </Typography>
    );
  } else if (imageFrame != null) {
    const { inferenceFrames } = imageFrame;

    switch (inferenceFrames?.type) {
      case "object-detection":
      case "zero-shot-object-detection": {
        typeSpecificControls = (
          <DetectionResultsControls
            panel={panel}
            results={inferenceFrames.current}
            classificationControls={
              <DetectionClassificationControls
                topic={topic}
                timestamp={imageFrame.timestamp}
                inferenceFrames={inferenceFrames}
              />
            }
          />
        );
        break;
      }
      case "image-segmentation": {
        typeSpecificControls = (
          <ImageSegmentationResultsControls
            panel={panel}
            results={inferenceFrames.current}
          />
        );
        break;
      }
      case "depth-estimation": {
        typeSpecificControls = <ImageResultsControls panel={panel} />;
        break;
      }
    }
  }

  return (
    <>
      <Typography variant="h6" component="p" sx={{ mb: 2 }}>
        Inference Tasks
      </Typography>
      <TopicSelect
        sx={{ mb: 1 }}
        inputLabel="Inference Topic"
        value={inferenceTopicSearchResult.topic ?? null}
        topics={inferenceTopics}
        onChange={handleInferenceTopicChange}
      />
      {inferenceTopicSearchResult.status === "missing" && (
        <Typography variant="subtitle2" component="p">
          {panel.inferenceTopicName} is not in this log
        </Typography>
      )}
      {typeSpecificControls}
    </>
  );
}

function filterInferenceTopics(
  baseTopic: Topic,
  playerTopics: ReadonlyArray<Topic>,
): Array<Topic> {
  return playerTopics.filter(
    (topic) =>
      topic.associatedTopicId === baseTopic.id && isInferenceTopic(topic),
  );
}
